import { useContext, useEffect, useState } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    FormGroup,
    Label,
    Input,
    FormFeedback,
    Card,
    CardBody,
    Row,
    Col,
} from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import IntlCurrencyInput from 'react-intl-currency-input';
import Dropzone, { type FileRejection } from 'react-dropzone';
import imageCompression from 'browser-image-compression';
import { currencyConfig } from '~/shared/components/form/Inputs';
import { RegistrationAddOn } from '~/types/campaign';
import { CampaignContext } from '~/context/campaign';
import {
    fetchFeatureImagePresignedUrl,
    uploadFile,
} from '~/services/campaign';
import { renderIcon } from '~/shared/components/icons';
import { getFilename } from '~/services/helper';
import { normalizeFileName } from '~/shared/utils';
import { Uploading } from '~/shared/components/Loading';
import { useToasts } from 'react-toast-notifications';
import { AdminContext } from '~/context/admin';
import {
    MAX_UPLOAD_BYTES,
    MISSING_CAMPAIGN_MESSAGE,
    describeRejections,
    resolveCampaignId,
    uploadErrorMessage,
} from './uploadHelpers';

type RegistrationAddOnFormData = {
    name: string;
    price: number;
    quantity: number | null;
    description: string;
    option_title: string;
    options: string;
    image: string | null;
    is_visible: boolean;
};

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onSave: (addon: Partial<RegistrationAddOn>) => void;
    addon?: RegistrationAddOn;
    title?: string;
};

const resizeImage = async (file: File) => {
    const options = {
        maxSizeMB: 2,
        maxWidthOrHeight: 2000,
        useWebWorker: true,
    };
    const compressedFile = await imageCompression(file, options);
    return compressedFile;
};

export const RegistrationAddOnModal = ({
    isOpen,
    onClose,
    onSave,
    addon,
    title,
}: Props) => {
    const [{ campaign }] = useContext(CampaignContext);
    const [{ campaignId: adminCampaignId }] = useContext(AdminContext);
    const { addToast } = useToasts();
    const [savingImage, setSavingImage] = useState(false);
    const [imgUrl, setImgUrl] = useState<string | null>(addon?.image || null);
    const isEditing = !!addon?.id;

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        clearErrors,
        formState: { errors },
    } = useForm<RegistrationAddOnFormData>({
        defaultValues: {
            name: addon?.name || '',
            price: addon?.price || 0,
            quantity: addon?.quantity || null,
            description: addon?.description || '',
            option_title: addon?.option_title || '',
            options: addon?.options || '',
            image: addon?.image || null,
            is_visible: addon?.is_visible ?? true,
        },
        mode: 'onSubmit',
    });

    const quantityValue = watch('quantity');

    useEffect(() => {
        if (isOpen) {
            clearErrors();
            if (addon) {
                reset({
                    name: addon.name || '',
                    price: addon.price || 0,
                    quantity: addon.quantity,
                    description: addon.description || '',
                    option_title: addon.option_title || '',
                    options: addon.options || '',
                    image: addon.image || null,
                    is_visible: addon.is_visible ?? true,
                });
                setImgUrl(addon.image || null);
            } else {
                reset({
                    name: '',
                    price: 0,
                    quantity: null,
                    description: '',
                    option_title: '',
                    options: '',
                    image: null,
                    is_visible: true,
                });
                setImgUrl(null);
            }
        }
    }, [isOpen, addon, reset, clearErrors]);

    const handleImageDropRejected = (rejections: FileRejection[]) => {
        addToast(describeRejections(rejections, 'a JPG, PNG or GIF image'), {
            appearance: 'error',
        });
    };

    const handleImageDrop = async (files: File[]) => {
        const campaignId = resolveCampaignId(campaign, adminCampaignId);
        if (!campaignId) {
            addToast(MISSING_CAMPAIGN_MESSAGE, { appearance: 'error' });
            return;
        }

        setSavingImage(true);
        let currentFile: File | undefined;
        try {
            for (const file of files) {
                currentFile = file;
                const fileName = normalizeFileName(file);
                const { fields, url } = await fetchFeatureImagePresignedUrl(
                    fileName,
                    campaignId
                );
                const resized = await resizeImage(file);
                await uploadFile(fields, url, resized);
                const fullUrl = url + fields.key;
                setValue('image', fullUrl);
                setImgUrl(fullUrl);
            }
        } catch (e) {
            console.error('Error uploading image:', e);
            addToast(uploadErrorMessage(e, currentFile), {
                appearance: 'error',
            });
        } finally {
            setSavingImage(false);
        }
    };

    const clearImage = () => {
        setValue('image', null);
        setImgUrl(null);
    };

    const onSubmit = (data: RegistrationAddOnFormData) => {
        const addonData: Partial<RegistrationAddOn> = {
            ...data,
            quantity: data.quantity || null,
        };

        if (addon?.id) {
            addonData.id = addon.id;
            addonData.isDirty = true;
        }

        onSave(addonData);
        onClose();
    };

    const handleClose = () => {
        reset();
        setImgUrl(null);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose} size="lg">
            <ModalHeader toggle={handleClose}>
                {title || (isEditing ? 'Edit Add-on' : 'Create Add-on')}
            </ModalHeader>
            <ModalBody>
                <p className="text-muted mb-4">
                    Add-ons can be anything from t-shirts of varying sizes to cookies.
                </p>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormGroup>
                        <Label for="name">
                            Name <span className="text-danger">*</span>
                        </Label>
                        <Controller
                            name="name"
                            control={control}
                            rules={{ required: 'Name is required' }}
                            render={({ field }) => (
                                <Input
                                    id="name"
                                    placeholder="T-Shirt"
                                    {...field}
                                    invalid={!!errors.name}
                                />
                            )}
                        />
                        {errors.name && (
                            <FormFeedback>{errors.name.message}</FormFeedback>
                        )}
                    </FormGroup>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="price">
                                    Price <span className="text-danger">*</span>
                                </Label>
                                <Controller
                                    name="price"
                                    control={control}
                                    rules={{
                                        required: 'Price is required',
                                        min: {
                                            value: 0,
                                            message: 'Price must be positive',
                                        },
                                    }}
                                    render={({ field }) => (
                                        <IntlCurrencyInput
                                            className={`form-control ${
                                                errors.price ? 'is-invalid' : ''
                                            }`}
                                            currency="USD"
                                            config={currencyConfig}
                                            value={field.value}
                                            onChange={(
                                                _event: any,
                                                value: number
                                            ) => field.onChange(value)}
                                        />
                                    )}
                                />
                                {errors.price && (
                                    <FormFeedback className="d-block">
                                        {errors.price.message}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="quantity">Quantity</Label>
                                <div className="d-flex align-items-center">
                                    <Controller
                                        name="quantity"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                id="quantity"
                                                type="number"
                                                min="0"
                                                placeholder="0"
                                                value={field.value ?? ''}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    field.onChange(val === '' ? null : parseInt(val));
                                                }}
                                                style={{ width: '100px' }}
                                            />
                                        )}
                                    />
                                    <span className="ms-2 text-muted">
                                        / {quantityValue || 0}
                                    </span>
                                </div>
                                <small className="text-muted">
                                    Leave blank for unlimited
                                </small>
                            </FormGroup>
                        </Col>
                    </Row>

                    <FormGroup>
                        <Label for="description">Description</Label>
                        <Controller
                            name="description"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    id="description"
                                    type="textarea"
                                    rows={4}
                                    placeholder="Text"
                                    {...field}
                                />
                            )}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="option_title">Option Title</Label>
                        <Controller
                            name="option_title"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    id="option_title"
                                    placeholder="Size"
                                    {...field}
                                />
                            )}
                        />
                        <small className="text-muted">
                            The label for the options dropdown, e.g., "Size"
                        </small>
                    </FormGroup>

                    <FormGroup>
                        <Label for="options">Options</Label>
                        <Controller
                            name="options"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    id="options"
                                    placeholder="2XL, XL, L, M, S, XS"
                                    {...field}
                                />
                            )}
                        />
                        <small className="text-muted">
                            Comma-separated list of options, e.g., "2XL, XL, L, M, S, XS"
                        </small>
                    </FormGroup>

                    <FormGroup>
                        <Label>Image</Label>
                        <Dropzone
                            onDrop={handleImageDrop}
                            onDropRejected={handleImageDropRejected}
                            maxSize={MAX_UPLOAD_BYTES}
                            accept={{ 'image/*': ['.png', '.jpg', '.jpeg', '.gif'] }}
                            multiple={false}
                            disabled={!!imgUrl}
                        >
                            {({ getRootProps, getInputProps }) => (
                                <Card
                                    style={{
                                        border: '2px dashed #949599',
                                        background: '#F0F6F6',
                                    }}
                                >
                                    <CardBody>
                                        <div {...getRootProps()}>
                                            <input {...getInputProps()} />
                                            <div className="text-center">
                                                {savingImage ? (
                                                    <Uploading />
                                                ) : imgUrl ? (
                                                    <div
                                                        className="position-relative mx-auto"
                                                        style={{
                                                            width: '220px',
                                                        }}
                                                    >
                                                        <img
                                                            src={imgUrl}
                                                            alt=""
                                                            style={{
                                                                height: '150px',
                                                                width: '150px',
                                                                objectFit:
                                                                    'cover',
                                                            }}
                                                        />
                                                        <div className="small my-2">
                                                            {getFilename(
                                                                imgUrl
                                                            )}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="text-danger fs-4 btn btn-link p-0 position-absolute top-0 end-0"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                clearImage();
                                                            }}
                                                        >
                                                            {renderIcon(
                                                                'close'
                                                            )}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="text-primary fs-4">
                                                            {renderIcon(
                                                                'upload'
                                                            )}
                                                        </div>
                                                        <p className="fs-6 mb-0">
                                                            <strong>
                                                                Click to upload
                                                            </strong>{' '}
                                                            or drag and drop
                                                            <br /> PNG, JPG, or
                                                            GIF (max 2000 x
                                                            2000px)
                                                        </p>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            )}
                        </Dropzone>
                    </FormGroup>

                    <FormGroup check className="mb-3">
                        <Controller
                            name="is_visible"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    type="switch"
                                    id="is_visible"
                                    checked={field.value}
                                    onChange={(e) =>
                                        field.onChange(e.target.checked)
                                    }
                                />
                            )}
                        />
                        <Label check for="is_visible" className="ms-2">
                            Visible to the public
                        </Label>
                    </FormGroup>
                </form>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    onClick={handleSubmit(onSubmit)}
                >
                    Save
                </Button>
            </ModalFooter>
        </Modal>
    );
};
